/**
 * DeviceBadge — small pill for the Master Player header (Item 5, Feb 2026).
 *
 * Two visual states:
 *   MASTER   fuchsia dot + "MASTER · N devices"   (this tab controls audio)
 *   REMOTE   amber dot + "REMOTE · Take Over"     (another tab has the crown)
 *
 * On REMOTE, the whole pill is a button — clicking it fires deviceContext.
 * takeover() which promotes this tab and (via the master gate in
 * MusicContext.play) unblocks local audio.
 *
 * When there's only ONE live device we render a minimal "MASTER" pill so
 * single-tab venues don't get a confusing "1 device" counter.
 */
import React from 'react';
import { Radio, RefreshCcw } from 'lucide-react';
import { useDevice } from '../contexts/DeviceContext';

export const DeviceBadge = ({ compact = false }) => {
  const { enabled, isMaster, devices, takeover } = useDevice();

  if (!enabled) return null;

  const total = devices.length || 1;
  const others = Math.max(0, total - 1);

  if (isMaster) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30 text-fuchsia-400 ${
          compact ? 'text-[9px] px-2 py-0.5' : 'text-[10px] px-2.5 py-1'
        } font-mono uppercase tracking-wider`}
        data-testid="device-badge-master"
        title={total > 1 ? `${total} devices connected` : 'Only this device'}
      >
        <Radio className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
        Master
        {others > 0 && (
          <span className="text-fuchsia-300/70">· +{others}</span>
        )}
      </span>
    );
  }

  return (
    <button
      onClick={takeover}
      className={`inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-500/50 text-amber-300 hover:bg-amber-500/25 active:scale-95 transition-all ${
        compact ? 'text-[9px] px-2 py-0.5' : 'text-[10px] px-2.5 py-1'
      } font-mono uppercase tracking-wider`}
      data-testid="device-badge-remote-takeover"
      title="Another tab is the audio Master. Click to take control here."
    >
      <RefreshCcw className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
      Remote · Take Over
    </button>
  );
};

export default DeviceBadge;
